"use client";

import { Suspense, useEffect } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import posthog from "posthog-js";
import {
  captureAttribution,
  clearAttribution,
  getStoredFirstTouch,
  getStoredLastTouch,
  type Attribution
} from "@/lib/adAttribution";
import { COOKIEBOT_ENABLED } from "@/lib/cookiebot";

function prefixed(
  attribution: Attribution | null,
  prefix: string
): Record<string, unknown> {
  if (!attribution) return {};
  return Object.fromEntries(
    Object.entries(attribution).map(([k, v]) => [`${prefix}${k}`, v])
  );
}

function pushToPostHog() {
  const first = getStoredFirstTouch();
  const last = getStoredLastTouch();
  // Use super-properties (register / register_once) instead of person
  // properties — anonymous users don't have person profiles in
  // identified_only mode, but super properties work either way.
  if (first) posthog.register_once(prefixed(first, "first_touch_"));
  if (last) posthog.register(prefixed(last, "last_touch_"));
}

function hasMarketingConsent(): boolean {
  if (!COOKIEBOT_ENABLED) return true;
  const consent = window.Cookiebot?.consent;
  return Boolean(consent?.marketing);
}

function AttributionTrackerInner() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Re-run on every navigation (App Router doesn't reload on route change).
  useEffect(() => {
    if (!hasMarketingConsent()) return;
    captureAttribution();
    if (posthog.has_opted_in_capturing()) {
      pushToPostHog();
    }
  }, [pathname, searchParams]);

  // Listen for consent changes — late consenters still get attribution from
  // the URL they're currently on, late de-consenters get their stored
  // attribution wiped.
  useEffect(() => {
    if (!COOKIEBOT_ENABLED) return;

    const onChange = () => {
      if (hasMarketingConsent()) {
        captureAttribution();
        if (posthog.has_opted_in_capturing()) pushToPostHog();
      } else {
        clearAttribution();
      }
    };

    window.addEventListener("CookiebotOnAccept", onChange);
    window.addEventListener("CookiebotOnDecline", onChange);
    window.addEventListener("CookiebotOnConsentReady", onChange);
    return () => {
      window.removeEventListener("CookiebotOnAccept", onChange);
      window.removeEventListener("CookiebotOnDecline", onChange);
      window.removeEventListener("CookiebotOnConsentReady", onChange);
    };
  }, []);

  return null;
}

export function AttributionTracker() {
  // Wrapped in Suspense because useSearchParams() requires it under
  // Next.js App Router.
  return (
    <Suspense fallback={null}>
      <AttributionTrackerInner />
    </Suspense>
  );
}
